import { Box, Typography, useTheme, alpha } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AccountBalanceWallet, Savings, ShowChart, TrendingFlat } from "@mui/icons-material";
import { useUtility } from "../../../core/hooks/useUtility";
import TradingDeskCharts from "./TradingDeskCharts";

const StatChip = ({ label, value, icon: Icon }) => {
  const theme = useTheme();
  const p = theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: { xs: 1, sm: 1.25 },
        border: `1px solid ${alpha(p, 0.1)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
          {label}
        </Typography>
        {Icon && (
          <Box sx={{ color: p, opacity: 0.85, display: "flex" }}>
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        )}
      </Box>
      <Typography
        variant="h6"
        fontWeight={800}
        noWrap
        title={value != null ? String(value) : undefined}
        sx={{ lineHeight: 1.2, fontSize: { xs: "0.95rem", sm: "1.25rem" } }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
};

const TradingSummaryPanel = ({ data, boughtList, showCharts = true }) => {
  const theme = useTheme();
  const { getColor } = useUtility();
  const pl = data?.currentPL;
  const plNum = typeof pl === "string" ? Number(String(pl).replace(/[^0-9.-]/g, "")) : pl;

  const summaryCard = (
    <Box
      sx={{
        borderRadius: 2,
        p: { xs: 1.25, sm: 1.5 },
        height: "100%",
        background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
          theme.palette.secondary?.main ?? theme.palette.primary.main,
          0.06
        )} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
      }}
    >
      <Typography variant="overline" color="text.secondary" fontWeight={800}>
        Net P&amp;L
      </Typography>
      <Typography
        variant="h4"
        fontWeight={900}
        sx={{
          color: getColor(plNum ?? 0, 0),
          letterSpacing: "-0.02em",
          mt: 0.25,
          fontSize: { xs: "1.6rem", sm: "2.125rem" },
          overflowWrap: "anywhere",
        }}
      >
        {data?.currentPL ?? "—"}
      </Typography>
      <Grid container spacing={1} sx={{ mt: 1.5 }}>
        <Grid size={6}>
          <StatChip label="Budget" value={data?.tradeBudget} icon={AccountBalanceWallet} />
        </Grid>
        <Grid size={6}>
          <StatChip label="Utilization" value={data?.utilization} icon={ShowChart} />
        </Grid>
        <Grid size={6}>
          <StatChip label="Balance" value={data?.tradeBalance} icon={Savings} />
        </Grid>
        <Grid size={6}>
          <StatChip label="Current value" value={data?.currentValue} icon={TrendingFlat} />
        </Grid>
      </Grid>
    </Box>
  );

  if (!showCharts) {
    return summaryCard;
  }

  return (
    <Box>
      <Grid container spacing={1.25} alignItems="stretch">
        <Grid size={{ xs: 12, md: 4 }}>
          {summaryCard}
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TradingDeskCharts summary={data} boughtList={boughtList} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TradingSummaryPanel;
